import {
  ButtonPrimeReact,
  DropdownPrimeReact,
  PrimeReactModal,
} from "@finanedi.es/storybook"
import { useGetDetalleAmortizacionActivoById } from "api/Activo.hook"
import {
  PostAndPutActivoAmortizacionInfoRequestBodyProps,
  postActivoAmortizacionInfo,
  putActivoAmortizacionInfo,
} from "api/Activo.service"
import {
  useCuentaContableEmpresaActivoAmortizacionAcumulada,
  useCuentaContableEmpresaActivoAmortizacionGastos,
} from "api/CuentaContableEmpresa.hook"
import { GetAllCoeficientesAmortizacionDtoProps } from "api/DTOs/GetAllCoeficientesAmortizacionDto"
import { GetDetalleAmortizacionActivoByIdDtoProps } from "api/DTOs/GetDetalleAmortizacionActivoByIdDto"
import { GetAllCoeficientesAmortizacionDtoIPagedEnumerableProps } from "api/Schemas/GetAllCoeficientesAmortizacionDtoIPagedEnumerable"
import { PERIODO_AMORTIZACION } from "api/Types/PeriodoAmortizacion"
import { TIPO_AMORTIZACION_ACTIVO } from "api/Types/TipoAmortizacionActivo"
import { TIPO_INMOVILIZADO } from "api/Types/TipoInmobilizado"
import { DropdownCuentasContablesConModal } from "common/components/DropdownsCuentasContables/DropdownCuentasContablesConModal/DropdownCuentasContablesConModal"
import { MODAL_STYLES } from "constants/primereactStyles"
import { ErrorMessage, Formik } from "formik"
import { InputNumber } from "primereact/inputnumber"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import * as Yup from "yup"
import { tipoAmortizacion } from "../../constants/tipoAmortizacion"
import { tipoPeriodoAmortizacion } from "../../constants/tipoPeriodo"

export const ModalAmortizar = ({
  isOpen = false,
  onCancel,
  onSuccess = () => undefined,
  coeficientesAmortizacion,
}: {
  isOpen: boolean
  onCancel: () => void
  onSuccess?: () => void
  coeficientesAmortizacion:
    | GetAllCoeficientesAmortizacionDtoIPagedEnumerableProps
    | undefined
}) => {
  const { t } = useTranslation()
  const { activoId } = useParams<{ activoId: string }>()
  const { detalleAmortizacionActivo } =
    useGetDetalleAmortizacionActivoById(activoId)

  const tipoPeriodoOptions = [
    {
      label: tipoPeriodoAmortizacion(t)[PERIODO_AMORTIZACION.MENSUAL],
      value: PERIODO_AMORTIZACION.MENSUAL,
    },
    {
      label: tipoPeriodoAmortizacion(t)[PERIODO_AMORTIZACION.ANUAL],
      value: PERIODO_AMORTIZACION.ANUAL,
    },
  ]

  const tipoOptions = [
    {
      label: tipoAmortizacion(t)[TIPO_AMORTIZACION_ACTIVO.LINEAL],
      value: TIPO_AMORTIZACION_ACTIVO.LINEAL,
    },
    {
      label: tipoAmortizacion(t)[TIPO_AMORTIZACION_ACTIVO.CRECIENTE],
      value: TIPO_AMORTIZACION_ACTIVO.CRECIENTE,
    },
    {
      label: tipoAmortizacion(t)[TIPO_AMORTIZACION_ACTIVO.DECRECIENTE],
      value: TIPO_AMORTIZACION_ACTIVO.DECRECIENTE,
    },
  ]

  const {
    cuentasContablesEmpresas: cuentasContablesActivoAmortizacionAcumulada,
  } = useCuentaContableEmpresaActivoAmortizacionAcumulada()
  const { cuentasContablesEmpresas: cuentasContablesActivoAmortizacionGastos } =
    useCuentaContableEmpresaActivoAmortizacionGastos()

  const crearActivoSchema = Yup.object().shape({
    conceptoAmortizacion: Yup.object()
      .shape({
        categoria: Yup.string().nullable(),
        coeficienteLinealMaximo: Yup.number().nullable(),
        id: Yup.string()
          .nullable()
          .required(`${t("formik.msg.requiredField")}`),
        nombre: Yup.string().nullable(),
        periodoEnAnyos: Yup.number().nullable(),
      })
      .nullable(),
    tipoPeriodo: Yup.number()
      .nullable()
      .required(`${t("formik.msg.requiredField")}`),
    coeficiente: Yup.number()
      .nullable()
      .required(`${t("formik.msg.requiredField")}`),
    anyos: Yup.number()
      .nullable()
      .required(`${t("formik.msg.requiredField")}`),
    cuentaContableAcumuladaId: Yup.string()
      .nullable()
      .required(`${t("formik.msg.requiredField")}`),
    cuentaContableGastoId: Yup.string()
      .nullable()
      .required(`${t("formik.msg.requiredField")}`),
    tipo: Yup.number()
      .nullable()
      .required(`${t("formik.msg.requiredField")}`),
  })

  const adapterAmortizacionEdit = (
    data: GetDetalleAmortizacionActivoByIdDtoProps | undefined
  ) => ({
    anyos: data?.anyos,
    tipoPeriodo: data?.tipoPeriodo ?? PERIODO_AMORTIZACION.ANUAL,
    tipo: data?.tipo ?? TIPO_INMOVILIZADO.MATERIAL,
    coeficiente: data?.coeficienteAmortizacion,
    cuentaContableAcumuladaId: data?.cuentaContableAcumulada?.id,
    cuentaContableGastoId: data?.cuentaContableGasto?.id,
    conceptoAmortizacion: coeficientesAmortizacion?.items?.find(
      (item: GetAllCoeficientesAmortizacionDtoProps) =>
        item.nombre === data?.concepto
    ),
  })

  return (
    <Formik
      validationSchema={crearActivoSchema}
      initialValues={adapterAmortizacionEdit(detalleAmortizacionActivo)}
      onSubmit={(values, { resetForm }) => {
        try {
          const requestBody: PostAndPutActivoAmortizacionInfoRequestBodyProps =
            {
              id: activoId,
              anyos: values.anyos,
              coeficiente: values.coeficiente,
              coeficienteAmortizacionId: values.conceptoAmortizacion?.id,
              cuentaContableAcumuladaId: values.cuentaContableAcumuladaId,
              cuentaContableGastoId: values.cuentaContableGastoId,
              tipo: values.tipo,
              tipoPeriodo: values.tipoPeriodo,
            }
          detalleAmortizacionActivo?.id
            ? putActivoAmortizacionInfo(requestBody).then(() => {
                resetForm({ values })
                onSuccess()
              })
            : postActivoAmortizacionInfo(requestBody).then(() => {
                resetForm({ values })
                onSuccess()
              })
        } catch (error) {
          console.error(error) // eslint-disable-line no-console
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        handleChange,
        handleSubmit,
        handleReset,
      }) => (
        <PrimeReactModal
          visible={isOpen}
          className='tw-w-128 tw-rounded-lg tw-py-5 tw-px-6 tw-bg-white'
          baseZIndex={20}
          ptContext={MODAL_STYLES}
          closable={false}
          keepInViewport={false}
          header={`${
            detalleAmortizacionActivo?.id
              ? t("nsAssets.modalAmortize.edit")
              : t("nsAssets.modalAmortize.generate")
          } ${t("nsAssets.modalAmortize.title")}`}
          footer={
            <div
              key='Continuar'
              className='tw-pb-1 tw-flex tw-flex-col sm:tw-flex-row sm:tw-gap-4 tw-justify-end tw-pt-3'
            >
              <ButtonPrimeReact
                type='reset'
                label={`${t("nsAssets.modalAmortize.cancel")}`}
                className='tw-w-full md:tw-w-fit tw-h-12 !tw-bg-simple-10 !tw-text-primary hover:!tw-text-primary-over !tw-border-none tw-items-center'
                onClick={() => {
                  onCancel()
                  detalleAmortizacionActivo?.id && handleReset()
                }}
                form='createActivo'
              />
              <ButtonPrimeReact
                type='submit'
                label={`${
                  detalleAmortizacionActivo?.id
                    ? t("nsAssets.modalAmortize.edit")
                    : t("nsAssets.modalAmortize.generate")
                }`}
                className='tw-w-full md:tw-w-fit !tw-h-12 !tw-bg-secondary hover:!tw-bg-secondary-over !tw-text-simple-10 !tw-border-none tw-items-center'
                onClick={() => handleSubmit()}
              />
            </div>
          }
        >
          <form
            className='tw-w-full tw-flex tw-flex-col tw-text-base tw-font-normal tw-gap-4'
            name='amortizarActivo'
          >
            <div className='tw-flex tw-flex-col tw-gap-2'>
              <label
                htmlFor='conceptoAmortizacion'
                className='!tw-mb-0 tw-w-full'
              >
                {t("nsAssets.modalAmortize.amortizationTableConcept")}
                <DropdownPrimeReact
                  id='conceptoAmortizacion'
                  name='conceptoAmortizacion'
                  className={`tw-h-10 ${
                    errors.conceptoAmortizacion && touched.conceptoAmortizacion
                      ? "tw-border-danger"
                      : "tw-border-neutral-25"
                  }`}
                  placeholder={`${t("nsAssets.modalAmortize.placeholder")}`}
                  onChange={(e) => {
                    handleChange(e)
                    setFieldValue(
                      "coeficiente",
                      e.value?.coeficienteLinealMaximo
                    )
                    setFieldValue("anyos", e.value?.periodoEnAnyos)
                  }}
                  filterInputAutoFocus
                  value={values.conceptoAmortizacion}
                  valueTemplate={(option) =>
                    option?.nombre
                      ? `${option.nombre} (${option?.coeficienteLinealMaximo}%)`
                      : undefined
                  }
                  itemTemplate={(option) =>
                    `${option?.nombre} (${option?.coeficienteLinealMaximo}%)`
                  }
                  optionLabel='nombre'
                  emptyFilterMessage={`${t(
                    "nsAssets.modalAmortize.emptyFilterMessage"
                  )}`}
                  emptyMessage={`${t("nsAssets.modalAmortize.emptyMessage")}`}
                  options={coeficientesAmortizacion?.items ?? []}
                  filterPlaceholder={`${t("cuentasContables.searchAccount")}`}
                  filter
                />
                <ErrorMessage
                  name='conceptoAmortizacion.id'
                  className='tw-text-danger tw-text-left'
                  component='div'
                />
              </label>
            </div>
            <div className='tw-grid tw-grid-cols-4 tw-gap-2'>
              <label
                htmlFor='tipoPeriodo'
                className='!tw-mb-0 tw-w-full tw-col-span-2'
              >
                {t("nsAssets.modalAmortize.amortizationPeriod")}
                <DropdownPrimeReact
                  id='tipoPeriodo'
                  name='tipoPeriodo'
                  className={`${
                    errors.tipoPeriodo && touched.tipoPeriodo
                      ? "tw-border-danger"
                      : "tw-border-neutral-25"
                  }`}
                  placeholder={`${t("nsAssets.modalAmortize.placeholder")}`}
                  onChange={handleChange}
                  value={values?.tipoPeriodo}
                  optionLabel='label'
                  optionValue='value'
                  emptyFilterMessage={`${t(
                    "nsAssets.modalAmortize.emptyFilterMessage"
                  )}`}
                  options={tipoPeriodoOptions}
                />
                <ErrorMessage
                  name='tipoPeriodo'
                  className='tw-text-danger tw-text-left'
                  component='div'
                />
              </label>
              <label
                htmlFor='coeficiente'
                className='!tw-mb-0 tw-w-full tw-col-span-1'
              >
                {t("nsAssets.modalAmortize.coefficient")}
                <InputNumber
                  inputId='coeficiente'
                  name='coeficiente'
                  inputClassName={`tw-w-full tw-text-right ${
                    errors.coeficiente && touched.coeficiente
                      ? "!tw-border-danger"
                      : "!tw-border-neutral-25"
                  }`}
                  className='tw-w-full'
                  onChange={(e) => {
                    setFieldValue("coeficiente", e.value)
                  }}
                  value={values?.coeficiente}
                  maxFractionDigits={0}
                  minFractionDigits={0}
                  min={0}
                />
                <ErrorMessage
                  name='coeficiente'
                  className='tw-text-danger tw-text-left'
                  component='div'
                />
              </label>
              <label
                htmlFor='anyos'
                className='!tw-mb-0 tw-w-full tw-col-span-1'
              >
                {t("nsAssets.modalAmortize.years")}
                <InputNumber
                  inputId='anyos'
                  name='anyos'
                  inputClassName={`tw-w-full ${
                    errors.anyos && touched.anyos
                      ? "!tw-border-danger"
                      : "!tw-border-neutral-25"
                  }`}
                  className='tw-w-full'
                  onChange={(e) => {
                    setFieldValue("anyos", e.value)
                  }}
                  value={values?.anyos}
                  maxFractionDigits={0}
                  minFractionDigits={0}
                  min={0}
                />
                <ErrorMessage
                  name='anyos'
                  className='tw-text-danger tw-text-left'
                  component='div'
                />
              </label>
            </div>
            <div className='tw-flex tw-flex-col tw-gap-2'>
              <label
                htmlFor='cuentaContableAcumuladaId'
                className='!tw-mb-0'
              >
                {`${t(
                  "nsAssets.modalAmortize.accumulatedAmortizationAccount"
                )}`}
              </label>
              <DropdownCuentasContablesConModal
                cuentasContablesOptions={
                  cuentasContablesActivoAmortizacionAcumulada
                }
                inputIdName='cuentaContableAcumuladaId'
                dropdownValue={values.cuentaContableAcumuladaId}
                onChange={handleChange}
                errors={errors}
                touched={touched}
              />
              <ErrorMessage
                name='cuentaContableAcumuladaId'
                className='tw-text-danger tw-text-left'
                component='div'
              />
            </div>
            <div className='tw-flex tw-flex-col tw-gap-2'>
              <label
                htmlFor='cuentaContableGastoId'
                className='!tw-mb-0'
              >
                {`${t("nsAssets.modalAmortize.amortizationExpenseAccount")}`}
              </label>
              <DropdownCuentasContablesConModal
                cuentasContablesOptions={
                  cuentasContablesActivoAmortizacionGastos
                }
                inputIdName='cuentaContableGastoId'
                dropdownValue={values.cuentaContableGastoId}
                onChange={handleChange}
                errors={errors}
                touched={touched}
              />
              <ErrorMessage
                name='cuentaContableGastoId'
                className='tw-text-danger tw-text-left'
                component='div'
              />
            </div>
            <div className='tw-flex tw-flex-col tw-gap-2'>
              <label
                htmlFor='tipo'
                className='!tw-mb-0'
              >
                {`${t("nsAssets.modalAmortize.amortizationType")}`}
              </label>
              <DropdownPrimeReact
                id='tipo'
                name='tipo'
                className={`${
                  errors.tipo && touched.tipo
                    ? "!tw-border-danger"
                    : "!tw-border-neutral-25"
                }`}
                placeholder={`${t("nsAssets.modalAmortize.placeholder")}`}
                onChange={handleChange}
                value={values?.tipo}
                options={tipoOptions}
              />
              <ErrorMessage
                name='tipo'
                className='tw-text-danger tw-text-left'
                component='div'
              />
            </div>
          </form>
        </PrimeReactModal>
      )}
    </Formik>
  )
}
